import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Percent } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const CPMCalculator = () => {
  const [totalCost, setTotalCost] = useState<string>("");
  const [impressions, setImpressions] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const costNum = parseFloat(totalCost) || 0;
    const impressionsNum = parseFloat(impressions) || 0;
    
    if (impressionsNum === 0) {
      setResult(null);
      return;
    }

    const cpm = (costNum / impressionsNum) * 1000;
    setResult(cpm);
  };

  const reset = () => {
    setTotalCost("");
    setImpressions("");
    setResult(null);
  };

  const relatedTools = getRelatedTools("cpm-calculator");

  return (
    <ToolPageLayout
      title="CPM Calculator"
      description="Calculate your Cost Per Mille (thousand impressions) to measure the cost efficiency of brand awareness campaigns."
      metaDescription="CPM (Cost Per Mille) measures the cost to reach 1,000 people with your ads. It's the primary metric for evaluating brand awareness and display advertising efficiency."
      icon={Percent}
      slug="cpm-calculator"
      formula={{
        equation: "CPM = (Total Ad Spend / Impressions) × 1,000",
        variables: [
          { name: "Total Ad Spend", description: "Total amount spent on the advertising campaign" },
          { name: "Impressions", description: "Total number of times your ad was displayed" },
        ]
      }}
      example={{
        inputs: [
          { label: "Total Ad Spend", value: "$1,000" },
          { label: "Impressions", value: "250,000" },
        ],
        output: "$4.00 CPM",
        interpretation: "You're paying $4 to reach 1,000 people. This is a competitive rate for most display networks."
      }}
      improvements={[
        "Target higher-quality placements with engaged audiences",
        "Use frequency capping to avoid ad fatigue",
        "Test different ad sizes and formats",
        "Optimize creative for viewability",
        "Use contextual targeting for brand-safe placements",
        "Negotiate better rates with publishers for direct buys",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Paid Marketing", href: "/paid-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate CPM</CardTitle>
          <CardDescription>Enter your ad spend and impressions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="totalCost">Total Ad Spend ($)</Label>
            <Input
              id="totalCost"
              type="number"
              placeholder="1000"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="impressions">Total Impressions</Label>
            <Input
              id="impressions"
              type="number"
              placeholder="250000"
              value={impressions}
              onChange={(e) => setImpressions(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate CPM
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Cost Per 1,000 Impressions</p>
                <p className="text-4xl font-bold text-primary">
                  ${result.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default CPMCalculator;
